export interface TourStep {
  id: string;
  /** data-tour attribute value of the element to highlight. Omit for centered modal steps. */
  selector?: string;
  /** data-tour attribute value of the element the user must click to advance.
   *  Falls back to selector when omitted. Ignored for modal steps. */
  clickSelector?: string;
  /** How the step advances:
   *  - 'click'  (default) user clicks the highlighted element
   *  - 'select' user picks an option in the highlighted dropdown
   *  - 'next'   explanation only, user presses Next */
  advance?: 'click' | 'select' | 'next';
  /** Preferred side for the bubble. 'right' keeps the bubble clear of an open
   *  dropdown list that opens below the highlighted control. */
  side?: 'right' | 'auto';
  title: string;
  body: string;
  /** Centered modal step (no spotlight target). */
  modal?: boolean;
  /** Explanation steps may point at areas that only exist once something is
   *  selected. When the target never appears, move on instead of stalling. */
  skipIfMissing?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    modal: true,
    title: 'Welcome to Refify!',
    body: "Let's create your first reference in about 2 minutes — we'll walk you through it step by step.",
  },
  {
    id: 'create-paper',
    selector: 'create-paper',
    title: 'Create a paper',
    body: 'Papers are where your references live. Create one for each assignment, article or project.',
  },
  {
    id: 'create-paper-form',
    selector: 'create-paper-form',
    clickSelector: 'create-paper-submit',
    title: 'Name your paper',
    body: 'Type a title (description and keywords are optional), then click Create paper.',
  },
  {
    id: 'add-reference',
    selector: 'add-reference',
    title: 'Add a reference',
    body: 'Open the reference panel to build your first reference.',
  },
  {
    id: 'pick-style',
    selector: 'panel-style',
    advance: 'select',
    side: 'right',
    title: 'Choose a style',
    body: 'Pick the reference style your school or journal asks for, such as APA or Harvard.',
  },
  {
    id: 'pick-source',
    selector: 'panel-source',
    advance: 'select',
    side: 'right',
    title: 'Choose a source type',
    body: 'Tell us what you are referencing — a book, a journal article, a website, and so on.',
  },
  {
    id: 'explain-collection',
    selector: 'panel-collection',
    advance: 'next',
    title: 'Collection',
    body: 'A collection holds a set of styles. Switch collection here if you use styles from somewhere else.',
  },
  {
    id: 'explain-style',
    selector: 'panel-style',
    advance: 'next',
    title: 'Style',
    body: 'The style decides exactly how the finished reference looks. You can change it at any time.',
  },
  {
    id: 'explain-source',
    selector: 'panel-source',
    advance: 'next',
    title: 'Source type',
    body: 'The source type decides which fields you need to fill in for this particular reference.',
  },
  {
    id: 'explain-category',
    selector: 'panel-category',
    advance: 'next',
    title: 'Category',
    body: 'Choose where the reference belongs: reference list, bibliography, figures or tables.',
  },
  {
    id: 'explain-favorite',
    selector: 'panel-favorite',
    advance: 'next',
    title: 'Favourite',
    body: 'Star a reference to save it to your favourites so you can reuse it in other papers.',
  },
  {
    id: 'explain-search-fill',
    selector: 'panel-search-fill',
    advance: 'next',
    title: 'Search and fill',
    body: 'Search for a title, link or ISBN and the fields below are filled in for you automatically.',
  },
  {
    id: 'explain-preview',
    selector: 'panel-preview',
    advance: 'next',
    title: 'Live preview',
    body: 'This shows the finished reference as you type. You can click it to fine-tune spacing or punctuation.',
  },
  {
    id: 'explain-fields',
    selector: 'panel-fields',
    advance: 'next',
    title: 'Fill in fields',
    body: 'Type the details of your source here. Every change is reflected in the preview above.',
  },
  {
    id: 'explain-eye',
    selector: 'panel-eye',
    advance: 'next',
    skipIfMissing: true,
    title: 'Show or hide a field',
    body: 'Use the eye to leave a field out of the finished reference, or bring an alternative field back in.',
  },
  {
    id: 'explain-info',
    selector: 'panel-info',
    advance: 'next',
    skipIfMissing: true,
    title: 'Field help',
    body: 'Where you see this icon, hover it for guidance on what belongs in that field.',
  },
  {
    id: 'explain-save',
    selector: 'panel-save',
    advance: 'next',
    title: 'Add the reference',
    body: 'When everything looks right, save the reference to your paper here.',
  },
  {
    id: 'done',
    modal: true,
    title: "You're all set!",
    body: 'That is everything you need to get started. You can restart this tour anytime from the ? button in the top bar.',
  },
];

export const TEMPLATE_TOUR_STEPS: TourStep[] = [
  {
    id: 'tmpl-welcome',
    modal: true,
    title: 'Welcome to Templates',
    body: 'This is where the reference styles you use in your papers live. Everyone starts with a ready-made set, so you can begin referencing straight away — and here you can adjust them or build your own. Take a short look around.',
  },
  {
    id: 'tmpl-collections',
    selector: 'tmpl-collections',
    advance: 'next',
    skipIfMissing: true,
    title: 'Collections',
    body: 'A collection is a folder of styles. Your starter collection is already here — click a collection to open it.',
  },
  {
    id: 'tmpl-add-collection',
    selector: 'tmpl-add-collection',
    advance: 'next',
    skipIfMissing: true,
    title: 'New collection',
    body: 'Use the plus to create your own collection, for example one per subject or per school.',
  },
  {
    id: 'tmpl-styles',
    selector: 'tmpl-styles',
    advance: 'next',
    skipIfMissing: true,
    title: 'Styles',
    body: 'Inside a collection you find the styles, such as APA or Harvard. Click a style to work on it.',
  },
  {
    id: 'tmpl-import-style',
    selector: 'tmpl-import-style',
    advance: 'next',
    skipIfMissing: true,
    title: 'Import a style',
    body: 'Search a large public library of ready-made styles and bring one straight into your collection.',
  },
  {
    id: 'tmpl-add-style',
    selector: 'tmpl-add-style',
    advance: 'next',
    skipIfMissing: true,
    title: 'New style',
    body: 'Or start from scratch and build a style yourself with the plus button.',
  },
  {
    id: 'tmpl-sources',
    selector: 'tmpl-sources',
    advance: 'next',
    skipIfMissing: true,
    title: 'Sources',
    body: 'Each style has source types — book, journal article, website and so on. Pick one to edit how it is formatted.',
  },
  {
    id: 'tmpl-preview',
    selector: 'tmpl-preview',
    advance: 'next',
    skipIfMissing: true,
    title: 'Live preview',
    body: 'The preview shows exactly how a finished reference will look, and updates as you edit below.',
  },
  {
    id: 'tmpl-fields',
    selector: 'tmpl-fields',
    advance: 'next',
    skipIfMissing: true,
    title: 'Reference fields',
    body: 'These are the building blocks of the reference. Drag them to change the order, and edit the text and punctuation around each one.',
  },
  {
    id: 'tmpl-add-fields',
    selector: 'tmpl-add-fields',
    advance: 'next',
    skipIfMissing: true,
    title: 'Add fields',
    body: 'An editable field is filled in when you create a reference. A static field always shows the same text.',
  },
  {
    id: 'tmpl-name-fields',
    selector: 'tmpl-name-fields',
    advance: 'next',
    skipIfMissing: true,
    title: 'Name fields',
    body: 'Name fields handle authors and editors — how many are listed, initials, and the order of the names.',
  },
  {
    id: 'tmpl-save',
    selector: 'tmpl-save',
    advance: 'next',
    skipIfMissing: true,
    title: 'Save your changes',
    body: 'Changes are kept once you save here. Until then you will see an "Unsaved changes" mark.',
  },
  {
    id: 'tmpl-intext',
    selector: 'tmpl-intext',
    advance: 'next',
    skipIfMissing: true,
    title: 'In-text citation',
    body: 'Set up the short citation used inside your text, for example (Smith, 2024), and preview it above.',
  },
  {
    id: 'tmpl-done',
    modal: true,
    title: 'That is Templates',
    body: 'You can always start with the ready-made styles and adjust them later. Restart this tour anytime from the ? button in the top bar.',
  },
];

export type TourId = 'papers' | 'templates';

export const TOURS: Record<TourId, TourStep[]> = {
  papers: TOUR_STEPS,
  templates: TEMPLATE_TOUR_STEPS,
};
